import { Fragment, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DepartmentSchema,
  EmployeeSchema,
  PositionSchema,
  ReferenceMasterSchema,
  type EmployeeDto,
} from '@taqon/contracts';
import Link from '@mui/material/Link';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { SearchFilterBar } from '../../shared/ui/filters';
import { FormDialog, SearchableSelect, StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { ImportButton } from '../../shared/import/ImportButton';
import { exportHref } from '../../shared/export/export-href';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { UrlDimensionNote } from '../reporting/dimensions';
import { EmployeePicker } from '../finance/lookups';
import { useFilterEmployee } from './url-filters';
import {
  GENDER_LABELS,
  PROFILE_FIELDS,
  PROFILE_GROUPS,
  useEducationLevels,
  useEmployeeGrades,
  type ProfileField,
} from './profile';

const EMPLOYEES = '/api/v1/hr/employees';

/** A selectable master: what the pickers show. Inactive ones stay visible on existing records. */
interface Option {
  readonly id: string;
  readonly label: string;
  readonly active: boolean;
  /** An employee option's name, for the photo beside it. */
  readonly name?: string;
}

/**
 * The Employee Master (ERD §7.3; FRD-HRP-005 to HRP-013): the list, and — where the session may —
 * create and change through the API. The Employee ID is generated by the system (FRD-CORE-035),
 * so it is shown and never typed. Department, cost centre, location, position and reporting
 * manager are chosen from the real records; nobody types an internal id.
 *
 * Status is the Release 1 active / inactive state only (FRD-HRP-010). There is no delete.
 */
export function EmployeesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'hr.employee:create');
  // Status and the joining-date range are read from the URL, so a dashboard figure links to the
  // list behind it (FRD-RPT-007). No status in the URL means active; `all` means any.
  const [urlParams, setUrlParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const statusParam = urlParams.get('status') ?? 'active';
  const status = statusParam === 'all' ? '' : statusParam;
  const joiningFrom = urlParams.get('joining_from') ?? '';
  const joiningTo = urlParams.get('joining_to') ?? '';
  // Exits in a period (CLR-47): the dashboards' exits and turnover link here.
  const exitFrom = urlParams.get('exit_from') ?? '';
  const exitTo = urlParams.get('exit_to') ?? '';
  const departmentId = urlParams.get('department_id') ?? '';
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(urlParams);
    if (value === '' && key !== 'status') next.delete(key);
    else next.set(key, value === '' ? 'all' : value);
    setUrlParams(next);
    setPage(0);
  };
  // The name search is in the URL too, so a filtered list survives a refresh.
  const search = urlParams.get('q') ?? '';
  const setSearch = (value: string) => {
    const next = new URLSearchParams(urlParams);
    if (value === '') next.delete('q');
    else next.set('q', value);
    setUrlParams(next, { replace: true });
    setPage(0);
  };
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const references = useReferences();

  const params = new URLSearchParams({ sort: 'code' });
  if (status !== '') params.set('filter[status]', status);
  if (search.trim() !== '') params.set('filter[name]', search.trim());
  if (joiningFrom !== '') params.set('filter[joining_from]', joiningFrom);
  if (joiningTo !== '') params.set('filter[joining_to]', joiningTo);
  if (exitFrom !== '') params.set('filter[exit_from]', exitFrom);
  if (exitTo !== '') params.set('filter[exit_to]', exitTo);
  if (departmentId !== '') params.set('filter[department_id]', departmentId);
  // Paged on the server: the export takes the same filters without the page.
  const pageParams = new URLSearchParams(params);
  pageParams.set('page', String(page + 1));
  pageParams.set('pageSize', String(pageSize));
  const employees = useQuery({
    queryKey: ['hr', 'employees', 'list', pageParams.toString()],
    queryFn: () => getPage(`${EMPLOYEES}?${pageParams.toString()}`, EmployeeSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Employees"
        description="The employee master every module refers to."
        actions={
          <>
            <ExportButton recordType="hr.employee" href={exportHref(EMPLOYEES, params)} />
            <ImportButton recordType="hr.employee" />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={(event) => {
                  // The dialog returns focus here when it closes.
                  event.currentTarget.focus();
                  setCreating(true);
                }}
              >
                New employee
              </Button>
            )}
          </>
        }
      />
      <UrlDimensionNote keys={['department_id']} />
      <SearchFilterBar label="Search by name" value={search} onChange={setSearch}>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setFilter('status', e.target.value);
          }}
          // "All" is the empty value; without these the select would show it blank.
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="">All</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="date"
          label="Joined from"
          value={joiningFrom}
          onChange={(e) => {
            setFilter('joining_from', e.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          type="date"
          label="Joined to"
          value={joiningTo}
          onChange={(e) => {
            setFilter('joining_to', e.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </SearchFilterBar>
      {employees.isError && <ErrorState message={employees.error.message} />}
      <Section>
        {employees.isPending && <LoadingState label="Loading employees" />}
        {employees.data !== undefined && employees.data.items.length === 0 && (
          <EmptyState title="No employees found" />
        )}
        {employees.data !== undefined && employees.data.items.length > 0 && (
          <Table size="small" aria-label="Employees">
            <TableHead>
              <TableRow>
                <TableCell>Employee ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Reporting manager</TableCell>
                <TableCell>Joining date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.data.items.map((employee) => (
                <TableRow key={employee.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Link
                      component={RouterLink}
                      to={`/hr/employees/${employee.id}`}
                      sx={{ fontWeight: 600 }}
                    >
                      {employee.code}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <PersonName name={employee.name} employeeId={employee.id} />
                  </TableCell>
                  <TableCell>{labelOf(references.departments, employee.departmentId)}</TableCell>
                  <TableCell>{labelOf(references.positions, employee.positionId)}</TableCell>
                  <TableCell>
                    <ManagerName options={references.employees} id={employee.reportingManagerId} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{employee.joiningDate}</TableCell>
                  <TableCell>
                    <StatusChip status={employee.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {employees.data !== undefined && employees.data.total > 0 && (
          <TablePagination
            component="div"
            count={employees.data.total}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_, next) => {
              setPage(next);
            }}
            onRowsPerPageChange={(e) => {
              setPageSize(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </Section>

      {creating && (
        <EmployeeDialog
          references={references}
          readOnly={false}
          onClose={(saved) => {
            setCreating(false);
            // The new record opens on its own page, with its generated Employee ID.
            if (saved !== undefined) void navigate(`/hr/employees/${saved.id}?created=1`);
          }}
        />
      )}
    </Stack>
  );
}

export interface References {
  readonly departments: readonly Option[];
  readonly costCentres: readonly Option[];
  readonly locations: readonly Option[];
  readonly positions: readonly Option[];
  readonly employees: readonly Option[];
  /** Which pickers the session cannot fill, for an honest explanation instead of empty lists. */
  readonly missing: readonly string[];
}

/** The real records the pickers offer, each read through its own API and permission. */
export function useReferences(): References {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const departments = useQuery({
    queryKey: ['departments'],
    enabled: can('organization.department:view'),
    queryFn: () => getPage('/api/v1/organization/departments?pageSize=200', DepartmentSchema),
  });
  const costCentres = useQuery({
    queryKey: ['cost-centers'],
    enabled: can('organization.cost_center:view'),
    queryFn: () => getPage('/api/v1/organization/cost-centers?pageSize=200', ReferenceMasterSchema),
  });
  const locations = useQuery({
    queryKey: ['locations'],
    enabled: can('organization.location:view'),
    queryFn: () => getPage('/api/v1/organization/locations?pageSize=200', ReferenceMasterSchema),
  });
  const positions = useQuery({
    queryKey: ['hr', 'positions'],
    enabled: can('hr.position:view'),
    queryFn: () => getPage('/api/v1/hr/positions?pageSize=200', PositionSchema),
  });
  const employees = useQuery({
    queryKey: ['hr', 'employees', 'all'],
    queryFn: () => getPage(`${EMPLOYEES}?pageSize=200&sort=name`, EmployeeSchema),
  });

  const master = (rows: readonly { id: string; code: string; name: string; status: string }[]) =>
    rows.map((row) => ({
      id: row.id,
      label: `${row.code} — ${row.name}`,
      active: row.status === 'active',
    }));
  return {
    departments: master(departments.data?.items ?? []),
    costCentres: master(costCentres.data?.items ?? []),
    locations: master(locations.data?.items ?? []),
    positions: (positions.data?.items ?? []).map((row) => ({
      id: row.id,
      label: row.title,
      active: row.status === 'active',
    })),
    employees: (employees.data?.items ?? []).map((row) => ({
      id: row.id,
      label: `${row.code} — ${row.name}`,
      name: row.name,
      active: row.status === 'active',
    })),
    missing: [
      ['organization.department:view', 'departments'],
      ['organization.cost_center:view', 'cost centres'],
      ['organization.location:view', 'locations'],
      ['hr.position:view', 'positions'],
    ]
      .filter(([capability]) => !can(capability ?? ''))
      .map(([, label]) => label ?? ''),
  };
}

function ManagerName({ options, id }: { options: readonly Option[]; id: string | null }) {
  const option = id === null ? undefined : options.find((o) => o.id === id);
  if (option?.name === undefined) return labelOf(options, id);
  return (
    <PersonName name={option.name} employeeId={option.id}>
      {option.label}
    </PersonName>
  );
}

export function labelOf(options: readonly Option[], id: string | null): string {
  if (id === null) return '—';
  // A record the session may not read is not described rather than shown by internal id.
  return options.find((option) => option.id === id)?.label ?? 'Not visible';
}

/** A value the masking service replaced (SDD §10.3). It can be neither shown nor edited. */
export function isMasked(value: string | null): boolean {
  return value?.startsWith('••••') ?? false;
}

const TEXT_FIELDS = [
  ['contactEmail', 'Contact email'],
  ['contactPhone', 'Contact phone'],
  ['emergencyContactName', 'Emergency contact name'],
  ['emergencyContactPhone', 'Emergency contact phone'],
] as const;

/**
 * Identity & compliance (ERD §7.3 "identity details (S)", as confirmed by management on
 * 24 September 2026): an identifier and an expiry date per area. Every value is optional. The
 * identifiers are designated sensitive, so most people see them masked: a masked identifier is
 * never shown or re-sent, but a holder of the change permission may replace it with a new value.
 * No expiry warning or alert is derived here; that belongs to a later increment.
 */
export const IDENTITY_AREAS = [
  ['emiratesIdNumber', 'emiratesIdExpiryDate', 'Emirates ID number'],
  ['passportNumber', 'passportExpiryDate', 'Passport number'],
  ['residencePermitNumber', 'residencePermitExpiryDate', 'Visa / residence permit number'],
  ['workPermitNumber', 'workPermitExpiryDate', 'Labour / work permit number'],
] as const;
type IdentityField = (typeof IDENTITY_AREAS)[number][0] | (typeof IDENTITY_AREAS)[number][1];
const IDENTITY_FIELDS: readonly IdentityField[] = IDENTITY_AREAS.flatMap(([number, expiry]) => [
  number,
  expiry,
]);

interface FormValues extends Record<IdentityField | ProfileField, string> {
  name: string;
  joiningDate: string;
  contactEmail: string;
  contactPhone: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  departmentId: string;
  costCenterId: string;
  locationId: string;
  positionId: string;
  reportingManagerId: string;
  status: string;
}

function valuesOf(employee: EmployeeDto | undefined): FormValues {
  return {
    name: employee?.name ?? '',
    joiningDate: employee?.joiningDate ?? '',
    contactEmail: employee?.contactEmail ?? '',
    contactPhone: employee?.contactPhone ?? '',
    emergencyContactName: employee?.emergencyContactName ?? '',
    emergencyContactPhone: employee?.emergencyContactPhone ?? '',
    departmentId: employee?.departmentId ?? '',
    costCenterId: employee?.costCenterId ?? '',
    locationId: employee?.locationId ?? '',
    positionId: employee?.positionId ?? '',
    reportingManagerId: employee?.reportingManagerId ?? '',
    status: employee?.status ?? 'active',
    ...(Object.fromEntries(
      [...IDENTITY_FIELDS, ...PROFILE_FIELDS].map((field) => {
        const value = employee?.[field] ?? null;
        // A masked value is not a value: the form starts empty and sends nothing unless replaced.
        return [field, value === null || isMasked(value) ? '' : value];
      }),
    ) as Record<IdentityField | ProfileField, string>),
  };
}

/**
 * Create (no `employee`) or view / change one employee. A change sends only what changed — the API
 * refuses a no-op status change — with If-Match carrying the version the list issued. An emptied
 * optional field is sent as null, which the API reads as "clear it".
 */
export function EmployeeDialog({
  employee,
  references,
  readOnly,
  onClose,
}: {
  employee?: EmployeeDto;
  references: References;
  readOnly: boolean;
  /** Called with the saved record after a save, with nothing on cancel. */
  onClose: (saved?: EmployeeDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const original = valuesOf(employee);
  const [values, setValues] = useState<FormValues>(original);
  const set = (field: keyof FormValues) => (value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const creating = employee === undefined;
  const optional = new Set<keyof FormValues>([
    'contactEmail',
    'contactPhone',
    'emergencyContactName',
    'emergencyContactPhone',
    'positionId',
    'reportingManagerId',
    ...IDENTITY_FIELDS,
    ...PROFILE_FIELDS,
  ]);
  const body: Record<string, string | null> = {};
  for (const field of Object.keys(values) as (keyof FormValues)[]) {
    const value = values[field].trim();
    if (creating) {
      if (field !== 'status' && value !== '') body[field] = value;
    } else if (value !== original[field]) {
      body[field] = optional.has(field) && value === '' ? null : value;
    }
  }
  const complete = ['name', 'joiningDate', 'departmentId', 'costCenterId', 'locationId'].every(
    (field) => values[field as keyof FormValues].trim() !== '',
  );

  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(EMPLOYEES, body, EmployeeSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${EMPLOYEES}/${employee.id}`,
            body,
            EmployeeSchema,
            session.data?.csrfToken ?? '',
            employee.version,
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'employee'] });
      onClose(saved);
    },
  });

  const grades = useEmployeeGrades();
  const levels = useEducationLevels();

  const selectable = (options: readonly Option[], current: string) =>
    options.filter((option) => option.active || option.id === current);

  // The manager is chosen with the server-side employee search (active employees only,
  // FRD-HRP-010), so every employee can be found however many there are.
  const currentManager = useFilterEmployee(original.reportingManagerId, true);
  const [managerChoice, setManagerChoice] = useState<EmployeeDto | null | undefined>(undefined);
  const manager = managerChoice === undefined ? currentManager : managerChoice;

  const picker = (
    field: 'departmentId' | 'costCenterId' | 'locationId' | 'positionId',
    label: string,
    options: readonly Option[],
    required: boolean,
  ): ReactNode => (
    <SearchableSelect
      label={label}
      required={required}
      disabled={readOnly}
      value={values[field]}
      onChange={set(field)}
      options={[
        ...selectable(options, original[field]).map((option) => ({
          value: option.id,
          label: `${option.label}${option.active ? '' : ' (inactive)'}`,
        })),
        // A current value the session cannot read keeps its place without exposing its id.
        ...(values[field] !== '' && !options.some((option) => option.id === values[field])
          ? [{ value: values[field], label: 'Not visible' }]
          : []),
      ]}
    />
  );

  return (
    <FormDialog
      open
      title={
        creating ? 'New employee' : `${readOnly ? 'Employee' : 'Edit employee'} ${employee.code}`
      }
      submitLabel={creating ? 'Create employee' : 'Save changes'}
      canSubmit={!readOnly && complete && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {creating ? (
        <Alert severity="info" variant="outlined">
          The Employee ID is generated by the system when the employee is saved.
        </Alert>
      ) : (
        <TextField label="Employee ID" value={employee.code} disabled />
      )}
      {creating && references.missing.length > 0 && (
        <Alert severity="warning" variant="outlined">
          Choosing {references.missing.join(', ')} needs permission to view them.
        </Alert>
      )}
      <TextField
        label="Name"
        required
        autoFocus={creating}
        disabled={readOnly}
        value={values.name}
        onChange={(e) => {
          set('name')(e.target.value);
        }}
      />
      <TextField
        label="Joining date"
        type="date"
        required
        disabled={readOnly}
        value={values.joiningDate}
        onChange={(e) => {
          set('joiningDate')(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      {picker('departmentId', 'Department', references.departments, true)}
      {picker('costCenterId', 'Cost centre', references.costCentres, true)}
      {picker('locationId', 'Work location', references.locations, true)}
      {picker('positionId', 'Position', references.positions, false)}
      <EmployeePicker
        label="Reporting manager"
        value={manager}
        disabled={readOnly}
        helperText="Optional"
        onChange={(value) => {
          setManagerChoice(value);
          set('reportingManagerId')(value?.id ?? '');
        }}
      />
      {TEXT_FIELDS.map(([field, label]) => {
        const masked = isMasked(employee?.[field] ?? null);
        return (
          <TextField
            key={field}
            label={label}
            type={field === 'contactEmail' ? 'email' : 'text'}
            disabled={readOnly || masked}
            value={values[field]}
            helperText={masked ? 'Masked: you are not permitted to see this value.' : undefined}
            onChange={(e) => {
              set(field)(e.target.value);
            }}
          />
        );
      })}
      <Divider textAlign="left">
        <Typography variant="subtitle2" component="h3">
          Identity &amp; compliance
        </Typography>
      </Divider>
      {IDENTITY_AREAS.map(([numberField, expiryField, label]) => {
        const numberMasked = isMasked(employee?.[numberField] ?? null);
        const expiryMasked = isMasked(employee?.[expiryField] ?? null);
        return (
          <Stack key={numberField} direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={label}
              fullWidth
              disabled={readOnly}
              value={values[numberField]}
              placeholder={numberMasked ? '•••• masked' : undefined}
              helperText={
                numberMasked
                  ? readOnly
                    ? 'Masked: you are not permitted to see this value.'
                    : 'Masked. Enter a value only to replace it.'
                  : undefined
              }
              slotProps={{ inputLabel: numberMasked ? { shrink: true } : {} }}
              onChange={(e) => {
                set(numberField)(e.target.value);
              }}
            />
            <TextField
              label="Expiry date"
              type={expiryMasked ? 'text' : 'date'}
              sx={{ minWidth: { sm: 190 } }}
              disabled={readOnly || expiryMasked}
              value={expiryMasked ? (employee?.[expiryField] ?? '') : values[expiryField]}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { 'aria-label': `${label} expiry date` },
              }}
              onChange={(e) => {
                set(expiryField)(e.target.value);
              }}
            />
          </Stack>
        );
      })}
      {PROFILE_GROUPS.map((group) => (
        <Fragment key={group.title}>
          <Divider textAlign="left">
            <Typography variant="subtitle2" component="h3">
              {group.title}
            </Typography>
          </Divider>
          {group.fields.map(([field, label, kind]) => {
            const masked = isMasked(employee?.[field] ?? null);
            const maskedHelp = masked
              ? readOnly
                ? 'Masked: you are not permitted to see this value.'
                : 'Masked. Enter a value only to replace it.'
              : undefined;
            if (kind === 'gender') {
              return (
                <SearchableSelect
                  key={field}
                  label={label}
                  disabled={readOnly}
                  value={values.gender}
                  helperText={maskedHelp}
                  emptyLabel="Not recorded"
                  onChange={set('gender')}
                  options={Object.entries(GENDER_LABELS).map(([code, text]) => ({
                    value: code,
                    label: text,
                  }))}
                />
              );
            }
            if (kind === 'education') {
              const options = (levels.data ?? []).filter(
                (level) => level.active || level.valueCode === original.educationLevelCode,
              );
              return (
                <SearchableSelect
                  key={field}
                  label={label}
                  disabled={readOnly}
                  value={values.educationLevelCode}
                  helperText={
                    maskedHelp ??
                    (levels.data?.length === 0
                      ? 'No education levels are configured yet (Administration › List values).'
                      : undefined)
                  }
                  emptyLabel="Not recorded"
                  onChange={set('educationLevelCode')}
                  options={[
                    ...options.map((level) => ({
                      value: level.valueCode,
                      label: `${level.label}${level.active ? '' : ' (inactive)'}`,
                    })),
                    ...(values.educationLevelCode !== '' &&
                    !options.some((level) => level.valueCode === values.educationLevelCode)
                      ? [{ value: values.educationLevelCode, label: values.educationLevelCode }]
                      : []),
                  ]}
                />
              );
            }
            if (kind === 'grade') {
              const options = (grades.data ?? []).filter(
                (grade) => grade.active || grade.valueCode === original.gradeCode,
              );
              return (
                <SearchableSelect
                  key={field}
                  label={label}
                  disabled={readOnly}
                  value={values.gradeCode}
                  helperText={
                    grades.data?.length === 0
                      ? 'No grades are configured yet (Administration › List values).'
                      : undefined
                  }
                  onChange={set('gradeCode')}
                  options={[
                    ...options.map((grade) => ({
                      value: grade.valueCode,
                      label: `${grade.label}${grade.active ? '' : ' (inactive)'}`,
                    })),
                    // A current value missing from the list keeps its place.
                    ...(values.gradeCode !== '' &&
                    !options.some((grade) => grade.valueCode === values.gradeCode)
                      ? [{ value: values.gradeCode, label: values.gradeCode }]
                      : []),
                  ]}
                />
              );
            }
            return (
              <TextField
                key={field}
                label={label}
                type={kind === 'date' ? 'date' : 'text'}
                multiline={kind === 'multiline'}
                minRows={kind === 'multiline' ? 2 : undefined}
                disabled={readOnly}
                value={values[field]}
                placeholder={masked ? '•••• masked' : undefined}
                helperText={maskedHelp}
                slotProps={{ inputLabel: kind === 'date' || masked ? { shrink: true } : {} }}
                onChange={(e) => {
                  set(field)(e.target.value);
                }}
              />
            );
          })}
        </Fragment>
      ))}
      {!creating && (
        <SearchableSelect
          label="Employment status"
          required
          disabled={readOnly}
          value={values.status}
          onChange={set('status')}
          helperText="An inactive employee cannot be used in new transactions but stays on history."
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
      )}
      {readOnly && (
        <Typography variant="caption" color="text.secondary">
          You can view this record but not change it.
        </Typography>
      )}
    </FormDialog>
  );
}
